import { toRdPhone } from "@/utils/validation";

interface RDStationData {
  token_rdstation: string;
  identificador: string;
  email: string;
  nome: string;
  cf_assunto?: string;
  cf_mensagem?: string;
  cf_empresa?: string;
  cf_dominio?: string;
  cf_cnpj?: string;
  cf_nome_legal?: string;
  cf_nome_fantasia?: string;
  cf_nome_contato?: string;
  cf_email_contato?: string;
  cf_telefone_contato?: string;
}

interface RDStationResponse {
  success: boolean;
  message?: string;
}

/**
 * Etapas do checkout (signup/parceiros/contratar-conta) acompanhadas na RD Station,
 * usadas para medir evolução e abandono dos clientes no fluxo de cadastro.
 */
export type CheckoutStep =
  | "dadosdocontato"
  | "dadosdonegocio"
  | "planoepagamento";

const CHECKOUT_STEP_IDENTIFICADORES: Record<CheckoutStep, string> = {
  dadosdocontato: "yuha_checkout_dadosdocontato",
  dadosdonegocio: "yuha_checkout_dadosdonegocio",
  planoepagamento: "yuha_checkout_planoepagamento",
};

/** Sufixo do identificador para separar, no RD, eventos vindos de DEV e PROD. */
const getEnvironmentSuffix = (): string => {
  const hostname = window.location.hostname;
  const isDev =
    hostname === "dev.yuha.com.br" ||
    hostname === "localhost" ||
    hostname.includes("dev");
  return isDev ? "dev" : "prod";
};

/**
 * Envia o evento de uma etapa do checkout para o RDStation
 * @param step - Etapa do checkout (dadosdocontato, dadosdonegocio ou planoepagamento)
 * @param data - Dados do usuário/conta coletados até a etapa
 * @returns Promise<RDStationResponse>
 */
export const sendCheckoutStepToRDStation = async (
  step: CheckoutStep,
  data: {
    name: string;
    email: string;
    accountName?: string | null;
    domain?: string | null;
    cnpj?: string | null;
    legalName?: string | null;
    tradeName?: string | null;
    contactName?: string | null;
    contactEmail?: string | null;
    contactPhone?: string | null;
  },
): Promise<RDStationResponse> => {
  const rdStationData: RDStationData = {
    token_rdstation: "500328a7916b83d1f57a440657d6973a",
    identificador: `${CHECKOUT_STEP_IDENTIFICADORES[step]}_${getEnvironmentSuffix()}`,
    email: data.email,
    nome: data.name,
    cf_empresa: data.accountName || "",
    cf_dominio: data.domain || "",
    cf_cnpj: data.cnpj || "",
    cf_nome_legal: data.legalName || "",
    cf_nome_fantasia: data.tradeName || "",
    cf_nome_contato: data.contactName || "",
    cf_email_contato: data.contactEmail || "",
    cf_telefone_contato: data.contactPhone ? toRdPhone(data.contactPhone) : "",
  };

  try {
    const response = await fetch("https://www.rdstation.com.br/api/1.3/conversions", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "Authorization": "Bearer 500328a7916b83d1f57a440657d6973a"
      },
      body: JSON.stringify(rdStationData),
    });

    if (!response.ok) {
      throw new Error(`Erro ${response.status}: ${response.statusText}`);
    }

    return {
      success: true,
      message: "Dados enviados para RDStation com sucesso"
    };
  } catch (error) {
    return {
      success: false,
      message: error instanceof Error ? error.message : "Erro desconhecido ao enviar para RDStation"
    };
  }
};

/**
 * Envia formulário de ajuda para o RDStation
 * @param data - Dados do formulário de ajuda
 * @returns Promise<RDStationResponse>
 */
export const sendHelpToRDStation = async (data: {
  nome: string;
  email: string;
  telefone: string;
  assunto: string;
  mensagem: string;
}): Promise<RDStationResponse> => {
  const rdStationData: RDStationData = {
    token_rdstation: "500328a7916b83d1f57a440657d6973a",
    identificador: `ajudaDash`,
    email: data.email,
    nome: data.nome,
    cf_telefone_contato: data.telefone ? toRdPhone(data.telefone) : "",
    cf_assunto: data.assunto || "",
    cf_mensagem: data.mensagem || "",
  };

  try {
    const response = await fetch("https://www.rdstation.com.br/api/1.3/conversions", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "Authorization": "Bearer 500328a7916b83d1f57a440657d6973a"
      },
      body: JSON.stringify(rdStationData),
    });

    if (!response.ok) {
      throw new Error(`Erro ${response.status}: ${response.statusText}`);
    }

    return {
      success: true,
      message: "Mensagem de ajuda enviada para RDStation com sucesso"
    };
  } catch (error) {
    return {
      success: false,
      message: error instanceof Error ? error.message : "Erro desconhecido ao enviar mensagem de ajuda para RDStation"
    };
  }
};
